import {
    PropsWithChildren,
    useState,
    useRef,
    useEffect,
    ReactNode,
    SyntheticEvent
} from "react";

import { css } from "@compiled/react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import ChevronRightLargeIcon from "@atlaskit/icon/glyph/chevron-right-large";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";
import { TypographyStyles } from "@/libraries/wpl/foundations/src/typography-styles";

import { Lozenge, LozengeAppearance } from "../../lozenge/src/lozenge";

const detailsStyles = css({
    display: "flex",
    flexDirection: "column",
    gap: "8px"
});

// CSS to hide the default summary marker
const summaryStyles = css({
    "listStyle": "none",
    "cursor": "pointer",
    "display": "flex",
    "gap": token("space.050"),
    "&::-webkit-details-marker": {
        display: "none"
    },
    "&::-moz-details-marker": {
        display: "none"
    }
});

// CSS for the content area
const contentStyles = css({
    overflow: "hidden",
    transition: "max-height 0.15s ease-in-out",
    paddingInlineStart: `calc(${token("space.300")} + ${token("space.050")})`
});

const collapsedStyles = css({
    // necessary to make the scrollHeight 0 for the animation
    display: "none"
});

const expandedContentStyles = css({
    margin: 0,
    display: "block"
});

export type ChevronColors = "color.primary.blue";

export interface ExpandProps extends PropsWithChildren {
    eventDataName?: string;
    label: string | ReactNode;
    expandedLabelText?: string | ReactNode;
    boldLabel?: boolean;
    chevronColor?: ChevronColors;
    isExpanded?: boolean;
    lozengeText?: string;
    lozengeAppearance?: LozengeAppearance;
}

export const Expand = ({
    children,
    label,
    expandedLabelText,
    eventDataName,
    boldLabel,
    chevronColor,
    isExpanded = false,
    lozengeText,
    lozengeAppearance = "blue"
}: ExpandProps) => {
    const [expanded, setExpanded] = useState(isExpanded);
    const { createAnalyticsEvent } = useAnalyticsEvents();

    // contentRef controls the content element height upon expansion change
    const contentRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!contentRef.current) {
            return;
        }

        if (expanded) {
            contentRef.current.style.maxHeight = `${contentRef.current.scrollHeight}px`;
        } else {
            contentRef.current.style.maxHeight = "0";
        }
    }, [expanded]);

    useEffect(() => {
        setExpanded(isExpanded);
    }, [isExpanded]);

    const handleToggle = (event: SyntheticEvent<HTMLDetailsElement>) => {
        const detailsElement = event.currentTarget;
        const isOpen = detailsElement.open;

        setExpanded(isOpen);

        if (eventDataName) {
            const data = {
                name: eventDataName,
                direction: isOpen ? "open" : "close"
            };
            createAnalyticsEvent({
                eventName: "expandClicked",
                eventType: "ui",
                eventComponent: "button",
                event: "clicked",
                actionSubject: "expand",
                action: "clicked",
                attributes: data,
                data
            }).fire();
        }
    };

    return (
        <details css={detailsStyles} open={expanded} onToggle={handleToggle}>
            <summary css={summaryStyles}>
                {/* custom expand icon */}
                <span
                    css={[
                        css({
                            height: "24px",
                            width: "24px",
                            transform: expanded
                                ? "rotate(90deg)"
                                : "rotate(0deg)",
                            transition: "transform 0.1s ease-in-out"
                        }),
                        chevronColor && css({ color: wacToken(chevronColor) })
                    ]}
                >
                    <ChevronRightLargeIcon label="" size="medium" />
                </span>

                <TypographyStyles>
                    <p
                        className="body-sm"
                        css={css({
                            margin: 0,
                            marginBlockStart: "1px"
                        })}
                        style={{
                            fontWeight: boldLabel ? 700 : "inherit"
                        }}
                    >
                        {expanded && expandedLabelText
                            ? expandedLabelText
                            : label}{" "}
                        {lozengeText && (
                            <Lozenge
                                text={lozengeText}
                                appearance={lozengeAppearance}
                            />
                        )}
                    </p>
                </TypographyStyles>
            </summary>

            <div
                // id for custom styling in side-nav-collection component
                id="expand-content"
                ref={contentRef}
                css={[
                    contentStyles,
                    expanded && expandedContentStyles,
                    !expanded && collapsedStyles
                ]}
            >
                <TypographyStyles>
                    <div className="body-sm">{children}</div>
                </TypographyStyles>
            </div>
        </details>
    );
};
